import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { FaArrowRight } from "react-icons/fa";
import { Link } from "react-router-dom";
import { FaPeopleGroup, FaMapLocationDot, FaMountainSun, FaShieldHalved, FaHandsHolding, FaHeart } from "react-icons/fa6";
import CircularBadge from "../badge";
import "./CommunityImpact.css";

/* ── Animation helpers ──────────────────────────────────────── */
const ease = [0.22, 1, 0.36, 1];

const fadeUp = (delay = 0) => ({
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease, delay } },
});

const cardVar = (delay = 0) => ({
  hidden: { opacity: 0, y: 36 },
  show: { opacity: 1, y: 0, transition: { duration: 0.65, ease, delay } },
});

/* ── Animated number counter ────────────────────────────────── */
function useCounter(target, active, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!active) return;
    let startTs = null;
    const numeric = parseFloat(String(target).replace(/[^0-9.]/g, ""));
    const step = (ts) => {
      if (!startTs) startTs = ts;
      const p = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(2, -10 * p);
      setVal(Math.floor(eased * numeric));
      if (p < 1) requestAnimationFrame(step);
      else setVal(numeric);
    };
    requestAnimationFrame(step);
  }, [active, target, duration]);
  return val;
}

/* ── Stat card data ─────────────────────────────────────────── */
const STATS = [
  {
    id: "lives",
    Icon: FaPeopleGroup,
    iconBg: "#4a5e42",
    value: 1247,
    suffix: "",
    label: "Lives Impacted",
    desc: "Veterans, first responders, and families supported through healing.",
    accentColor: "#4a5e42",
  },
  {
    id: "states",
    Icon: FaMapLocationDot,
    iconBg: "#a86642",
    value: 32,
    suffix: "",
    label: "States Reached",
    desc: "Our community spans from coast to coast and continues to grow.",
    accentColor: "#a86642",
  },
  {
    id: "retreats",
    Icon: FaMountainSun,
    iconBg: "#3d5a6a",
    value: 9,
    suffix: "",
    label: "Retreats Completed",
    desc: "Transformative weeks of connection, reflection, and renewal.",
    accentColor: "#3d5a6a",
  },
  {
    id: "groups",
    Icon: FaShieldHalved,
    iconBg: "#5a4a7a",
    value: 6,
    suffix: "",
    label: "Community Groups",
    desc: "Supporting veterans, first responders, families, and volunteers.",
    accentColor: "#5a4a7a",
  },
  {
    id: "volunteers",
    Icon: FaHandsHolding,
    iconBg: "#8a7a2a",
    value: 456,
    suffix: "",
    label: "Volunteers",
    desc: "Generous souls donating time, energy, and heart to the mission.",
    accentColor: "#8a7a2a",
  },
  {
    id: "cities",
    Icon: FaHeart,
    iconBg: "#384638",
    value: 100,
    suffix: "+",
    label: "Cities Represented",
    desc: "A global brotherhood united by purpose and shared experience.",
    accentColor: "#384638",
  },
];

/* ── Single stat card ───────────────────────────────────────── */
function StatCard({ stat, delay, active }) {
  const { Icon } = stat;
  const count = useCounter(stat.value, active);
  const display = stat.value === 1247 ? count.toLocaleString() : String(count);

  return (
    <motion.div className="ci__card" variants={cardVar(delay)} initial="hidden" animate={active ? "show" : "hidden"}>
      {/* Coloured icon circle */}
      <div className="ci__card-icon" style={{ background: stat.iconBg }} aria-hidden="true">
        <Icon />
      </div>

      {/* Number */}
      <div className="ci__card-num" style={{ color: stat.accentColor }}>
        {display}
        {stat.suffix}
      </div>

      {/* Label + short rule */}
      <div className="ci__card-label-wrap">
        <p className="ci__card-label" style={{ color: stat.accentColor }}>
          {stat.label.toUpperCase()}
        </p>
        <span className="ci__card-rule" style={{ background: stat.accentColor }} aria-hidden="true" />
      </div>

      {/* Description */}
      <p className="ci__card-desc">{stat.desc}</p>

      {/* Bottom accent rule */}
      <span className="ci__card-bottom-rule" style={{ background: stat.accentColor }} aria-hidden="true" />
    </motion.div>
  );
}

/* ── Main component ─────────────────────────────────────────── */
export default function CommunityImpact({ showCta = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section className="ci" id="community-impact" ref={ref}>
      <div className="ci__inner container-fg container-2xxl">
        {/* ── Header ── */}
        <div className="ci__header">
          <motion.p className="ci__eyebrow u-eyebrow" variants={fadeUp(0)} initial="hidden" animate={inView ? "show" : "hidden"}>
            Community Impact
          </motion.p>

          {/* Logo mark between rules */}
          <motion.div className="ci__logo-divider" variants={fadeUp(0.06)} initial="hidden" animate={inView ? "show" : "hidden"} aria-hidden="true">
            <span className="ci__logo-rule" />
            <svg viewBox="0 0 30.45 26.53" className="ci__logo-mark" fill="var(--c-rust)">
              <polygon points="7.71 4.56 11.64 4.56 11.64 8.75 0 8.75 2.45 11.2 10.68 11.2 11.51 12.03 11.51 17.61 8.31 14.41 5.08 14.41 11.92 21.26 11.92 24.5 13.95 26.53 13.95 0 7.71 4.56" />
              <polygon points="22.74 4.56 18.81 4.56 18.81 8.75 30.45 8.75 28 11.2 19.77 11.2 18.94 12.03 18.94 17.61 22.13 14.41 25.37 14.41 18.52 21.26 18.52 24.5 16.5 26.53 16.5 0 22.74 4.56" />
            </svg>
            <span className="ci__logo-rule" />
          </motion.div>

          {/* Main headline */}
          <motion.h2 className="ci__headline u-display" variants={fadeUp(0.1)} initial="hidden" animate={inView ? "show" : "hidden"}>
            Our Journey Continues.
          </motion.h2>

          {/* Rust short rule */}
          <motion.span className="ci__head-rule" variants={fadeUp(0.14)} initial="hidden" animate={inView ? "show" : "hidden"} aria-hidden="true" />

          {/* Sub-copy */}
          <motion.p className="ci__sub" variants={fadeUp(0.17)} initial="hidden" animate={inView ? "show" : "hidden"}>
            Lives are being changed, bonds are being forged,
            <br />
            and a brotherhood is growing stronger every day.
          </motion.p>
        </div>

        {/* ── Six stat cards ── */}
        <div className="ci__grid">
          {STATS.map((stat, i) => (
            <StatCard key={stat.id} stat={stat} delay={0.06 + i * 0.08} active={inView} />
          ))}
        </div>

        {/* ── CTA ── */}
        {showCta && (
          <motion.div className="ci__cta-wrap" variants={fadeUp(0.55)} initial="hidden" animate={inView ? "show" : "hidden"}>
            <Link to="/community" className="ci__cta">
              Explore Our Community
              <FaArrowRight className="ci__cta-icon" aria-hidden="true" />
            </Link>
          </motion.div>
        )}
      </div>

      {/* Botanical leaf — bottom-left */}
      <motion.div className="ci__botanical" initial={{ opacity: 0, x: -20 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 1, delay: 0.4, ease }} aria-hidden="true">
        <img src="./assets/plant.png" alt="" />
      </motion.div>

      {/* Badge + real mountain image — bottom-right */}
      <motion.div className="ci__badge-corner" initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ duration: 1, delay: 0.5, ease }} aria-hidden="true">
        <CircularBadge colors="var(--c-rust)" />
        <img src="./assets/mountainsForst.png" alt="" className="ci__badge-mtn" />
      </motion.div>
    </section>
  );
}
